import {
  CaretDownOutlined,
  CaretUpOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { RadioInput } from '@mm/components';
import { Divider, InputNumber } from 'antd';
import React, { useState } from 'react';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { OnClickElsewhere } from '../../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import styles from './SelectAllLeadsButton.module.css';

type Props = {
  disabled: boolean;
  maxLeadsCount: number;
  count: number | undefined;
  setCount: (value: number | undefined) => void;
  onReset: () => void;
  onSelectAllClick: () => void;
};

export const SelectAllLeadsButton = ({
  disabled,
  maxLeadsCount,
  count,
  setCount,
  onReset,
  onSelectAllClick,
}: Props) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'all' | 'quantity'>(
    'all',
  );
  const [input, setInput] = useState<number | undefined>(undefined);
  const applyDisabled = selectedOption === 'quantity' && !input;

  const closeModal = () => setOpenModal(false);

  const onApplyClick = () => {
    if (selectedOption === 'all') {
      onSelectAllClick();
    }
    if (selectedOption === 'quantity') {
      onSelectAllClick();
      setCount(input);
    }
    closeModal();
  };

  const onResetAll = () => {
    onReset();
    setSelectedOption('all');
    setInput(undefined);
    closeModal();
  };

  return (
    <div className={styles.container}>
      <PrimaryButton
        disabled={disabled}
        className={styles.primaryButton}
        onClick={() => {
          setOpenModal(!openModal);
        }}
      >
        {count ? `${count.toLocaleString('en-US')} selected` : 'Select'}
        {openModal ? (
          <CaretUpOutlined className={styles.caret} />
        ) : (
          <CaretDownOutlined className={styles.caret} />
        )}
      </PrimaryButton>
      {openModal && (
        <OnClickElsewhere handleClick={closeModal}>
          <div className={styles.modalContainer}>
            <div className={styles.radioInpuWrapper}>
              <RadioInput
                className={styles.radioButton}
                name="all"
                label={`Select all ${maxLeadsCount.toLocaleString(
                  'en-US',
                )} leads`}
                checked={selectedOption === 'all'}
                onChange={() => {
                  setInput(undefined);
                  setSelectedOption('all');
                }}
                value="all"
              />
            </div>
            <div className={styles.radioInpuWrapper}>
              <RadioInput
                className={styles.radioButton}
                name="quantity"
                label="Select number of leads"
                checked={selectedOption === 'quantity'}
                onChange={() => {
                  setCount(undefined);
                  setSelectedOption('quantity');
                }}
                value="quantity"
              />
              <InputNumber
                min={1}
                max={maxLeadsCount}
                autoFocus
                defaultValue={input}
                value={input}
                onChange={value => setInput(value || 1)}
                status={!input ? 'error' : undefined}
                disabled={selectedOption !== 'quantity'}
              />
            </div>
            <div className={styles.buttons}>
              <PrimaryButton
                className={styles.primaryButton}
                disabled={applyDisabled}
                onClick={onApplyClick}
              >
                Apply
              </PrimaryButton>
              <PrimaryButton
                className={styles.secondaryButton}
                color="transparent-no-shadow"
                onClick={closeModal}
              >
                Cancel
              </PrimaryButton>
            </div>
            <Divider style={{ margin: '8px 0px' }} />
            <div className={styles.resetBlock} onClick={onResetAll}>
              <CloseOutlined />
              Reset all
            </div>
          </div>
        </OnClickElsewhere>
      )}
    </div>
  );
};
